import { environment } from '@core/environment';
import { useCachedUrl } from '@core/logic';
import { mapboxStyleLookup } from '@core/types';
import { invalidate } from '@react-three/fiber';
import { useAlkisWmsUrl } from '@viewer3D/components/Map/helper/wms-utils';
import { useEffect, useState } from 'react';
import type { Texture } from 'three';
import type { TileProps } from '../types';
import {
  getCachedAlkisTexture,
  getCachedBasemapTexture,
  loadAlkisTexture,
  loadBasemapTexture,
} from './tileTextureCache';

export interface ViewPort {
  min: {
    latitude: number;
    longitude: number;
    lat: number;
    lng: number;
  };
  max: {
    latitude: number;
    longitude: number;
    lat: number;
    lng: number;
  };
}

const buildTileUrl = ({ tileX, tileY, zoom, style }: TileProps): string | undefined => {
  const styleString = mapboxStyleLookup[style];
  if (!styleString) return undefined;
  return `https://api.mapbox.com/styles/v1/mapbox/${styleString}/tiles/${zoom}/${tileX}/${tileY}?access_token=${environment.mapBox.accessToken}`;
};

export const useTileTexture = (
  tile: TileProps,
): { texture: Texture | undefined; alkisTexture: Texture | undefined } => {
  // For ALKIS style, use the new hook to get the first successful WMS URL
  const alkisTextureUrl = useAlkisWmsUrl(tile.tileX, tile.tileY, tile.zoom);

  const tileUrl = buildTileUrl(tile);

  const textureUrl = useCachedUrl(tileUrl || undefined, false);

  // Synchronously initialize with cached texture if available (0ms frame-0 rendering)
  const [texture, setTexture] = useState<Texture | undefined>(() =>
    textureUrl ? getCachedBasemapTexture(textureUrl) : undefined,
  );
  const [alkisTexture, setAlkisTexture] = useState<Texture | undefined>(() =>
    alkisTextureUrl ? getCachedAlkisTexture(alkisTextureUrl) : undefined,
  );

  useEffect(() => {
    if (!textureUrl) {
      setTexture(undefined);
      return;
    }

    const cached = getCachedBasemapTexture(textureUrl);
    if (cached) {
      setTexture(cached);
      invalidate();
      return;
    }

    let cancelled = false;
    loadBasemapTexture(textureUrl)
      .then((loadedTexture) => {
        if (!cancelled) {
          setTexture(loadedTexture);
          invalidate();
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load basemap tile texture:', err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [textureUrl]);

  useEffect(() => {
    if (!alkisTextureUrl) {
      setAlkisTexture(undefined);
      return;
    }

    const cached = getCachedAlkisTexture(alkisTextureUrl);
    if (cached) {
      setAlkisTexture(cached);
      invalidate();
      return;
    }

    let cancelled = false;
    loadAlkisTexture(alkisTextureUrl)
      .then((loadedTexture) => {
        if (!cancelled) {
          setAlkisTexture(loadedTexture);
          invalidate();
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load ALKIS tile texture:', err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [alkisTextureUrl]);

  return {
    texture,
    alkisTexture,
  };
};
